import React, { useEffect, useRef, useState } from 'react';
import TextInput from '../TextInput';
import { formatHoursShort, parseDurationInput } from '../../utils/timesheetTime';

interface DurationInputProps {
  /** Committed hours, or null for none. */
  value: number | null;
  /** Called on blur or Enter with quarter-rounded hours, or null when cleared. */
  onCommit: (next: number | null) => void;
  size?: 'sm' | 'md';
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * An hours field that understands "1.5", "1:30", "1h30", "90m" and rounds
 * to the nearest quarter hour, the unit the database books in. Same local
 * state model as TimeInput: what is typed is not overwritten by a refetch
 * until the field is left. Escape restores.
 */
const DurationInput: React.FC<DurationInputProps> = ({
  value,
  onCommit,
  size = 'sm',
  invalid = false,
  disabled = false,
  placeholder = '0',
  id,
  autoFocus,
  'aria-label': ariaLabel,
  className = '',
  onKeyDown
}) => {
  const display = (v: number | null) => (v === null || v === 0 ? '' : formatHoursShort(v));
  const [text, setText] = useState(() => display(value));
  const [unreadable, setUnreadable] = useState(false);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) {
      setText(display(value));
      setUnreadable(false);
    }
  }, [value]);

  const commit = () => {
    const parsed = parseDurationInput(text);
    if (parsed === 'invalid') {
      setUnreadable(true);
      return;
    }
    setUnreadable(false);
    setText(display(parsed));
    if (parsed !== value) {
      onCommit(parsed);
    }
  };

  return (
    <TextInput
      id={id}
      size={size}
      mono
      inputMode="decimal"
      autoComplete="off"
      autoFocus={autoFocus}
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      invalid={invalid || unreadable}
      title={unreadable ? 'Not a duration. Try 1.5, 1:30 or 90m. Escape puts back the saved value.' : undefined}
      aria-label={ariaLabel}
      className={`duration-input ${className}`.trim()}
      onFocus={(e) => {
        focused.current = true;
        e.currentTarget.select();
      }}
      onChange={(e) => {
        setText(e.target.value);
        if (unreadable) setUnreadable(false);
      }}
      onBlur={() => {
        focused.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setText(display(value));
          setUnreadable(false);
          e.currentTarget.blur();
        }
        onKeyDown?.(e);
      }}
    />
  );
};

export default DurationInput;
